/**
 * PatientPathwayTimeline (Sprint 4.3.2, PROD-651).
 *
 * Chronological list of all activities for a patient, ordered by
 * `start_date`. Filterable by activity type (action_def_id), status,
 * and stakeholder (indirect_object). Embedded in a sub-panel of the
 * editor and toggleable from Runtime Mode only.
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/docs/
 *   getting-started/orchestration-ontology.md
 *   ("...you can thus look at activities as a full history log of what
 *    has happened in a pathway.")
 *
 * Data flow: activities are passed in as a prop (parent fetches via
 * `getFhirClient().searchTasks` + `bundleToPathwayActivities`). The
 * timeline does NOT issue its own FHIR call so it stays deterministic
 * + test-friendly. The parent owns the loading / error state.
 *
 * HC1 reproducibility: the sort + filter are pure; given the same
 * fixture seed the timeline renders the same row order across reads.
 * HC2 token-only.
 * HC5 zero em / en dashes anywhere.
 * HC6 a11y: filter selects carry `htmlFor` labels; the list region is
 * `role="region"` with an `aria-labelledby` pointing at the title.
 */
import { useId, useMemo, useState } from 'react';
import {
  PATHWAY_ACTIVITY_STATUSES,
  type PathwayActivity,
  type PathwayActivityStatus,
} from '../../types/pathway-activity';
import { sortActivitiesByStartDate } from '../../services/pathway-activities';
import { ActivityStatusPill } from './ActivityInspector';

export interface PatientPathwayTimelineProps {
  activities: PathwayActivity[];
  className?: string;
}

const ANY_VALUE = '__any__';

export function PatientPathwayTimeline({
  activities,
  className,
}: PatientPathwayTimelineProps) {
  const titleId = useId();
  const typeFilterId = useId();
  const statusFilterId = useId();
  const stakeholderFilterId = useId();

  const [typeFilter, setTypeFilter] = useState<string>(ANY_VALUE);
  const [statusFilter, setStatusFilter] = useState<string>(ANY_VALUE);
  const [stakeholderFilter, setStakeholderFilter] = useState<string>(ANY_VALUE);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const a of activities) {
      if (a.action_def_id) set.add(a.action_def_id);
    }
    return Array.from(set).sort();
  }, [activities]);

  const stakeholderOptions = useMemo(() => {
    const set = new Set<string>();
    for (const a of activities) {
      if (a.indirect_object) set.add(a.indirect_object);
    }
    return Array.from(set).sort();
  }, [activities]);

  const visible = useMemo(() => {
    const filtered = activities.filter((a) => {
      if (typeFilter !== ANY_VALUE && a.action_def_id !== typeFilter) {
        return false;
      }
      if (statusFilter !== ANY_VALUE && a.status !== statusFilter) {
        return false;
      }
      if (
        stakeholderFilter !== ANY_VALUE &&
        a.indirect_object !== stakeholderFilter
      ) {
        return false;
      }
      return true;
    });
    return sortActivitiesByStartDate(filtered);
  }, [activities, typeFilter, statusFilter, stakeholderFilter]);

  return (
    <section
      role="region"
      aria-labelledby={titleId}
      data-testid="patient-pathway-timeline"
      data-activity-count={visible.length}
      className={[
        'flex flex-col gap-gutter-sm border-t border-surface-border bg-surface-base px-gutter-md py-gutter-sm',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-baseline justify-between gap-gutter-md">
        <h3
          id={titleId}
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Patient pathway timeline
        </h3>
        <span className="text-signal-xs text-text-tertiary">
          {visible.length} of {activities.length} activities
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-gutter-md">
        <FilterSelect
          id={typeFilterId}
          label="Activity type"
          value={typeFilter}
          onChange={setTypeFilter}
          options={typeOptions}
          testId="timeline-filter-type"
        />
        <FilterSelect
          id={statusFilterId}
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={PATHWAY_ACTIVITY_STATUSES.slice()}
          testId="timeline-filter-status"
        />
        <FilterSelect
          id={stakeholderFilterId}
          label="Stakeholder"
          value={stakeholderFilter}
          onChange={setStakeholderFilter}
          options={stakeholderOptions}
          testId="timeline-filter-stakeholder"
        />
      </div>
      {visible.length === 0 ? (
        <p
          data-testid="patient-pathway-timeline-empty"
          className="text-signal-sm text-text-tertiary"
        >
          No activities match the active filters.
        </p>
      ) : (
        <ol
          data-testid="patient-pathway-timeline-list"
          className="flex flex-col gap-1"
        >
          {visible.map((a) => (
            <TimelineRow key={a.activity_id} activity={a} />
          ))}
        </ol>
      )}
    </section>
  );
}

interface FilterSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: ReadonlyArray<string | PathwayActivityStatus>;
  testId: string;
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
  testId,
}: FilterSelectProps) {
  return (
    <div className="flex items-center gap-1">
      <label
        htmlFor={id}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        {label}
      </label>
      <select
        id={id}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        <option value={ANY_VALUE}>Any</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TimelineRowProps {
  activity: PathwayActivity;
}

function TimelineRow({ activity }: TimelineRowProps) {
  return (
    <li
      data-testid={`patient-pathway-timeline-row-${activity.activity_id}`}
      data-activity-id={activity.activity_id}
      data-status={activity.status}
      data-action-def-id={activity.action_def_id}
      className="flex items-center justify-between gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-muted px-gutter-sm py-1"
    >
      <div className="flex flex-col">
        <span className="text-signal-sm font-semibold text-text-primary">
          {activity.label ?? activity.activity_id}
        </span>
        <span className="text-signal-xs text-text-tertiary">
          {activity.start_date ?? 'Not started'} | {activity.indirect_object}
        </span>
      </div>
      <ActivityStatusPill status={activity.status} compact />
    </li>
  );
}

export { ANY_VALUE as TIMELINE_ANY_VALUE };
